import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  AlertCircle,
} from 'lucide-react'
import { useRequestContext } from './RequestDetailLayout'
import { formatDate } from '../../utils/dateUtils'

export function RequestHistoryTab() {
  const { request } = useRequestContext()

  if (!request) return null

  const formatTimeLocal = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Construir timeline de eventos
  const events = [
    {
      date: request.requestedAt,
      title: 'Solicitud creada',
      description: `Solicitud de ${request.daysRequested} días creada`,
      icon: FileText,
      color: 'primary',
    },
  ]

  if (request.processedAt) {
    events.push({
      date: request.processedAt,
      title: request.status === 'APPROVED' ? 'Solicitud aprobada' : 'Solicitud denegada',
      description: request.status === 'DENIED' && request.denialReason
        ? `Motivo: ${request.denialReason}`
        : request.status === 'APPROVED'
          ? 'La solicitud fue aprobada correctamente'
          : 'La solicitud fue denegada',
      icon: request.status === 'APPROVED' ? CheckCircle : XCircle,
      color: request.status === 'APPROVED' ? 'success' : 'error',
    })
  }

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-success-100 dark:bg-success-900/30',
          text: 'text-success-600',
          line: 'bg-success-200 dark:bg-success-800',
        }
      case 'error':
        return {
          bg: 'bg-error-100 dark:bg-error-900/30',
          text: 'text-error-600',
          line: 'bg-error-200 dark:bg-error-800',
        }
      case 'warning':
        return {
          bg: 'bg-warning-100 dark:bg-warning-900/30',
          text: 'text-warning-600',
          line: 'bg-warning-200 dark:bg-warning-800',
        }
      default:
        return {
          bg: 'bg-primary-100 dark:bg-primary-900/30',
          text: 'text-primary-600',
          line: 'bg-primary-200 dark:bg-primary-800',
        }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Historial de la Solicitud
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Seguimiento de eventos y cambios de estado
        </p>
      </div>

      {/* Timeline */}
      <div className="card">
        <div className="relative">
          {events.map((event, index) => {
            const colors = getColorClasses(event.color)
            const IconComponent = event.icon
            const isLast = index === events.length - 1

            return (
              <div key={index} className="relative flex gap-4">
                {/* Timeline Line */}
                {!isLast && (
                  <div
                    className={`absolute left-4 top-10 w-0.5 h-full ${colors.line}`}
                    style={{ transform: 'translateX(-50%)' }}
                  />
                )}

                {/* Icon */}
                <div className={`relative z-10 w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className={`h-4 w-4 ${colors.text}`} />
                </div>

                {/* Content */}
                <div className={`flex-1 ${!isLast ? 'pb-8' : ''}`}>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {event.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {event.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{formatDate(event.date)}</span>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatTimeLocal(event.date)}</span>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Estado actual si está pendiente */}
          {request.status === 'PENDING' && (
            <div className="relative flex gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <div className="relative z-10 w-8 h-8 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center flex-shrink-0 animate-pulse">
                <Clock className="h-4 w-4 text-warning-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Pendiente de revisión
                  <span className="badge badge-warning text-xs">En espera</span>
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  La solicitud está pendiente de ser procesada por un administrador.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detalles de fechas */}
      <div className="card">
        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
          Resumen de Fechas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Fecha de Inicio</span>
            </div>
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatDate(request.startDate)}
            </p>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Fecha de Fin</span>
            </div>
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatDate(request.endDate)}
            </p>
          </div>

          <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
            <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Duración Total</span>
            </div>
            <p className="font-bold text-2xl text-primary-600 dark:text-primary-400">
              {request.daysRequested} días
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
